import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { Plus, Minus, HelpCircle, Sparkles } from 'lucide-react';

const FAQPage = () => {
  const [activeIndex, setActiveIndex] = useState<number | null>(0);

  const faqs = [
    {
      q: "How long do the braids typically last?",
      a: "Our signature braids are designed for longevity. Generally, our styles last between 6 to 10 weeks depending on the style and your hair texture. We provide every client with a bespoke maintenance ritual to maximize durability."
    },
    {
      q: "Do you provide the hair extensions?",
      a: "Yes. For our premium experience, we supply ultra-grade hypoallergenic braiding hair in a variety of curated shades. This ensures a consistent, high-end finish that protects your scalp."
    },
    {
      q: "Is there a strategy for scalp health?",
      a: "Scalp health is our primary metric. We use botanical pH-balanced cleansers and tension-free techniques. Our goal is beauty without sacrifice."
    },
    {
      q: "What is your appointment policy?",
      a: "As a boutique studio, we operate on a high-demand schedule. Appointments require a small confirmation fee via phone/SMS to secure your time. We request 48 hours notice for rescheduling."
    },
    {
      q: "How should I prepare for my arrival?",
      a: "We recommend arriving with hair that has been detangled. We handle the deep hydration and scalp preparation as part of our elite service."
    }
  ];

  return (
    <div className="pt-24 pb-32 bg-cream min-h-screen">
      <div className="max-w-4xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-24"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 border border-gold/20 mb-6 text-gold font-bold text-[10px] uppercase tracking-[0.3em]">
            <HelpCircle size={14} />
            The Intelligence Hub
          </div>
          <h1 className="text-6xl md:text-8xl font-serif font-bold text-dark tracking-tighter mb-8 italic">
            Curated <br /><span className="text-gold not-italic">Insights</span>
          </h1>
          <p className="text-xl text-gray-500 max-w-2xl mx-auto font-light leading-relaxed">
            Clarity in every interaction. Understanding the Moyhe process.
          </p>
        </motion.div>

        <div className="space-y-6">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={`rounded-[30px] border transition-all duration-500 overflow-hidden ${activeIndex === index
                ? 'bg-dark border-gold/30 shadow-2xl'
                : 'bg-white border-gold/10 shadow-sm hover:border-gold/30'
                }`}
            >
              <button
                onClick={() => setActiveIndex(activeIndex === index ? null : index)}
                className="w-full px-10 py-10 flex items-center justify-between text-left"
              >
                <div className="flex items-center gap-6">
                  <span className={`font-serif text-3xl italic transition-colors duration-500 ${activeIndex === index ? 'text-gold' : 'text-gray-200'}`}>
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className={`text-xl font-bold tracking-tight transition-colors duration-500 ${activeIndex === index ? 'text-white' : 'text-dark'}`}>
                    {faq.q}
                  </span>
                </div>
                <div className={`p-2 rounded-full transition-all duration-500 ${activeIndex === index ? 'bg-gold text-dark' : 'bg-gold/5 text-gold'}`}>
                  {activeIndex === index ? <Minus size={20} /> : <Plus size={20} />}
                </div>
              </button>

              <AnimatePresence>
                {activeIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <div className="px-10 pb-10 ml-14">
                      <div className="h-[1px] w-20 bg-gold/20 mb-8"></div>
                      <p className="text-gray-400 text-lg leading-relaxed font-light">
                        {faq.a}
                      </p>
                      <div className="mt-8 flex items-center gap-2">
                        <Sparkles size={12} className="text-gold" />
                        <span className="text-[10px] uppercase font-bold tracking-[0.2em] text-gold/60">Professional Guidance</span>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FAQPage;
